/**
 * Terminal.ts — xterm.js wrapper providing a clean API for game rendering.
 *
 * Abstracts away xterm.js internals and provides typed methods for:
 * - Writing colored text
 * - Cursor positioning
 * - Screen clearing
 * - Viewport resize handling
 * - Raw input access (for InputManager to consume)
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { type ColorName, theme, RESET } from './ColorTheme';

export type KeyHandler = (key: string, domEvent: KeyboardEvent) => void;
export type ResizeHandler = (cols: number, rows: number) => void;

export class Terminal {
  private xterm: XTerm;
  private fitAddon: FitAddon;
  private container: HTMLElement;
  private resizeHandlers: ResizeHandler[] = [];

  constructor(container: HTMLElement) {
    this.container = container;

    this.xterm = new XTerm({
      theme: {
        background: '#000000',
        foreground: '#cccccc',
        cursor: '#cccccc',
        cursorAccent: '#000000',
        selectionBackground: '#444444',
      },
      fontFamily: '"Cascadia Mono", "Fira Code", "Consolas", "Courier New", monospace',
      fontSize: 16,
      lineHeight: 1.2,
      cursorBlink: true,
      cursorStyle: 'block',
      allowProposedApi: true,
      scrollback: 0,
      convertEol: true,
    });

    this.fitAddon = new FitAddon();
    this.xterm.loadAddon(this.fitAddon);
    this.xterm.open(container);
    this.fitAddon.fit();

    // Handle window resize
    const resizeObserver = new ResizeObserver(() => {
      this.fitAddon.fit();
      const { cols, rows } = this.getSize();
      for (const handler of this.resizeHandlers) {
        handler(cols, rows);
      }
    });
    resizeObserver.observe(container);
  }

  // --- Writing ---

  /** Write text at current cursor position (no newline) */
  write(text: string, color?: ColorName): void {
    if (color) {
      this.xterm.write(`${theme.fg(color)}${text}${RESET}`);
    } else {
      this.xterm.write(text);
    }
  }

  /** Write text followed by a newline */
  writeLine(text: string, color?: ColorName): void {
    this.write(text, color);
    this.xterm.write('\r\n');
  }

  /** Write pre-formatted text (already contains ANSI codes) */
  writeRaw(text: string): void {
    this.xterm.write(text);
  }

  /** Write a blank line */
  blankLine(): void {
    this.xterm.write('\r\n');
  }

  /** Write a horizontal separator line */
  separator(char = '-', color?: ColorName): void {
    const { cols } = this.getSize();
    const line = char.repeat(cols);
    this.writeLine(line, color ?? 'frostSilver');
  }

  // --- Cursor & Screen ---

  /** Clear the entire terminal viewport */
  clear(): void {
    this.xterm.clear();
    this.xterm.write('\x1b[H\x1b[2J');
  }

  /**
   * Clear a rectangular region of the terminal with spaces.
   * @param startRow First row to clear (1-based)
   * @param endRow Last row to clear (1-based)
   * @param startCol Optional first column (1-based, default 1)
   * @param width Optional width in characters (default: full row)
   */
  clearRegion(startRow: number, endRow: number, startCol?: number, width?: number): void {
    const { cols } = this.getSize();
    const col = startCol ?? 1;
    const w = width ?? cols;
    const blank = ' '.repeat(w);
    for (let r = startRow; r <= endRow; r++) {
      this.xterm.write(`\x1b[${r};${col}H${blank}`);
    }
  }

  /**
   * Clear all rows from startRow to endRow (inclusive).
   * Used by MenuSystem for clearBelow functionality.
   * @param startRow First row to clear (1-based)
   * @param endRow Last row to clear (1-based, defaults to bottom of screen)
   */
  clearFromRow(startRow: number, endRow?: number): void {
    const { rows } = this.getSize();
    const lastRow = endRow ?? rows;
    for (let r = startRow; r <= lastRow; r++) {
      this.setCursor(r, 1);
      this.xterm.write('\x1b[2K');
    }
  }

  /**
   * Clear the current line at the cursor position.
   * Does not move the cursor.
   */
  clearLine(): void {
    this.xterm.write('\x1b[2K');
  }

  /**
   * Clear a specific line.
   * @param row The row to clear (1-based)
   */
  clearLineAt(row: number): void {
    this.setCursor(row, 1);
    this.xterm.write('\x1b[2K');
  }

  /** Get the terminal's container element. */
  getContainer(): HTMLElement {
    return this.container;
  }

  /** Move cursor to a specific row and column (1-based) */
  setCursor(row: number, col: number): void {
    this.xterm.write(`\x1b[${row};${col}H`);
  }

  /** Move cursor to the beginning of a specific row */
  setCursorRow(row: number): void {
    this.setCursor(row, 1);
  }

  /** Hide the cursor */
  hideCursor(): void {
    this.xterm.write('\x1b[?25l');
  }

  /** Show the cursor */
  showCursor(): void {
    this.xterm.write('\x1b[?25h');
  }

  /** Get current terminal dimensions */
  getSize(): { cols: number; rows: number } {
    return {
      cols: this.xterm.cols,
      rows: this.xterm.rows,
    };
  }

  /** Get the current cursor row (1-based, relative to viewport) */
  getCursorRow(): number {
    return this.xterm.buffer.active.cursorY + 1;
  }

  // --- Input ---

  /** Register a handler for raw key input (used by InputManager) */
  onKey(handler: KeyHandler): void {
    this.xterm.onKey(({ key, domEvent }) => {
      handler(key, domEvent);
    });
  }

  /** Register a handler for binary/data input (used for mouse events) */
  onData(handler: (data: string) => void): void {
    this.xterm.onData(handler);
  }

  /** Enable mouse tracking (for click and hover support) */
  enableMouseTracking(): void {
    this.xterm.write('\x1b[?1000h');
    this.xterm.write('\x1b[?1002h');
    this.xterm.write('\x1b[?1006h');
  }

  /** Disable mouse tracking */
  disableMouseTracking(): void {
    this.xterm.write('\x1b[?1000l');
    this.xterm.write('\x1b[?1002l');
    this.xterm.write('\x1b[?1006l');
  }

  // --- Resize ---

  /** Register a resize handler */
  onResize(handler: ResizeHandler): void {
    this.resizeHandlers.push(handler);
  }

  /** Remove a resize handler */
  offResize(handler: ResizeHandler): void {
    this.resizeHandlers = this.resizeHandlers.filter(h => h !== handler);
  }

  /** Force a re-fit to container */
  fit(): void {
    this.fitAddon.fit();
  }

  // --- Font ---

  /** Set font size (small=12, medium=16, large=20) */
  setFontSize(size: number): void {
    this.xterm.options.fontSize = size;
    this.fitAddon.fit();
  }

  getFontSize(): number {
    return this.xterm.options.fontSize ?? 16;
  }

  // --- Transitions ---

  /** Fade out the terminal container */
  async fadeOut(durationMs = 150): Promise<void> {
    this.container.style.transition = `opacity ${durationMs}ms ease-in-out`;
    this.container.style.opacity = '0';
    await new Promise(resolve => setTimeout(resolve, durationMs));
  }

  /** Fade in the terminal container */
  async fadeIn(durationMs = 150): Promise<void> {
    this.container.style.transition = `opacity ${durationMs}ms ease-in-out`;
    this.container.style.opacity = '1';
    await new Promise(resolve => setTimeout(resolve, durationMs));
  }

  // --- Cleanup ---

  /** Dispose of the terminal */
  dispose(): void {
    this.disableMouseTracking();
    this.xterm.dispose();
  }

  /** Get the underlying xterm instance (escape hatch) */
  getRawTerminal(): XTerm {
    return this.xterm;
  }

  /** Focus the terminal to receive keyboard input */
  focus(): void {
    this.xterm.focus();
  }
}
